import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";

const faqs = [
  {
    q: "天堂影视需要注册账号吗？",
    a: "不需要。天堂影视完全免费，无需注册即可在线观看所有电影资源，我们致力于提供最便捷的观影体验。",
  },
  {
    q: "天堂影视的电影画质如何？",
    a: "天堂影视提供高清蓝光画质，最高支持4K分辨率，确保您享受极致的观影体验。同时采用先进的视频编码技术，保证流畅播放不卡顿。",
  },
  {
    q: "天堂影视是否真的免广告？",
    a: "是的。天堂影视承诺全程免广告，您在观影过程中不会受到任何广告打扰，可以沉浸在纯粹的电影世界中。",
  },
  {
    q: "电影资源多久更新一次？",
    a: "天堂影视每日更新电影资源，第一时间收录最新上映的电影和高分佳作，确保您不会错过任何一部好电影。",
  },
  {
    q: "天堂影视支持哪些设备观看？",
    a: "天堂影视支持电脑、平板、手机等全设备在线观看，采用响应式设计，在不同屏幕尺寸上都能获得良好的观影体验。",
  },
  {
    q: "遇到播放问题怎么解决？",
    a: "如果遇到播放卡顿或无法播放的情况，建议检查网络连接或尝试切换清晰度。如问题持续，可通过下方联系方式与我们取得联系。",
  },
];

const testimonials = [
  {
    name: "电影爱好者小李",
    text: "天堂影视真的太棒了！画质清晰，完全不卡顿，而且真的没有广告，我现在每天都在这里看电影。",
    rating: 5,
  },
  {
    name: "资深影迷老张",
    text: "我用了很多电影网站，天堂影视是体验最好的。资源更新快，分类清晰，推荐功能也很准，强烈推荐！",
    rating: 5,
  },
  {
    name: "大学生小王",
    text: "宿舍网速一般，但天堂影视不缓冲，蓝光画质也能流畅播放。免费免注册，简直是学生的福音。",
    rating: 5,
  },
  {
    name: "宝妈阿芳",
    text: "晚上哄孩子睡了之后看部电影放松一下，天堂影视资源多，动画片也很多，大人小孩都能找到喜欢的。",
    rating: 4,
  },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <nav
              className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap"
              aria-label="面包屑导航"
            >
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>

            <section className="mb-14">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                关于天堂影视
              </h1>
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    <strong className="text-foreground-900">天堂影视</strong>
                    成立于2020年，是一个专注于提供高清蓝光电影在线观看的影视平台。我们致力于为广大电影爱好者打造一个免广告、不缓冲、不卡顿的纯粹观影环境，让每一位用户都能享受到影院级的观影体验。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    天堂影视收录超过
                    <strong className="text-foreground-900">10,000部</strong>
                    电影资源，涵盖动作片、喜剧片、爱情片、科幻片、恐怖片、动画片、纪录片、战争片、悬疑片、犯罪片等十大分类，每日更新最新电影和高分佳作。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的使命是让每一个人都能免费享受高品质的电影内容。天堂影视，让观影回归纯粹。
                  </p>
                </div>
                <div className="md:w-60 shrink-0">
                  <div className="rounded-lg overflow-hidden bg-background-200 aspect-square">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20movie%20theater%20interior%20with%20warm%20golden%20lighting%2C%20comfortable%20cinema%20seats%2C%20dark%20walls%20with%20subtle%20texture%2C%20luxurious%20and%20inviting%20atmosphere%2C%20soft%20ambient%20light%2C%20cinematic%20quality%2C%20no%20text%2C%208k&width=400&height=400&seq=about-us-logo&orientation=squarish"
                      alt="天堂影视"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    icon: "ri-4k-line",
                    title: "高清蓝光",
                    desc: "支持4K蓝光画质，极致清晰",
                  },
                  {
                    icon: "ri-speed-up-line",
                    title: "不缓冲不卡顿",
                    desc: "先进CDN加速，流畅播放",
                  },
                  {
                    icon: "ri-advertisement-line",
                    title: "全程免广告",
                    desc: "纯净观影，零打扰体验",
                  },
                  {
                    icon: "ri-refresh-line",
                    title: "每日更新",
                    desc: "最新最热电影第一时间上线",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-5 rounded-lg bg-background-100 text-center"
                  >
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                      <i className={`${item.icon} text-primary-500 text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, si) => (
                        <i
                          key={si}
                          className={`text-xs ${
                            si < t.rating
                              ? "ri-star-fill text-primary-500"
                              : "ri-star-line text-foreground-300"
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      {t.text}
                    </p>
                    <span className="text-xs text-foreground-400 font-medium">
                      — {t.name}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-lg bg-background-100 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                    >
                      <span className="text-sm font-medium text-foreground-900 pr-4">
                        {faq.q}
                      </span>
                      <i
                        className={`ri-${
                          openFaq === i ? "subtract" : "add"
                        }-line text-foreground-400 text-lg shrink-0 transition-transform duration-200`}
                      ></i>
                    </button>
                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        openFaq === i ? "max-h-64 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                    <i className="ri-mail-line text-primary-500 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@tiantangmovies.com</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                    <i className="ri-wechat-line text-primary-500 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-500">天堂影视官方</p>
                </div>
                <div className="p-5 rounded-lg bg-background-100 text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-lg bg-primary-500/10 mb-3">
                    <i className="ri-customer-service-line text-primary-500 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服支持</h3>
                  <p className="text-xs text-foreground-500">工作日 9:00 - 18:00</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于天堂影视 - 电影大全免费在线观看高清蓝光",
            url: "https://tiantangmovies.com/about",
            description:
              "天堂影视成立于2020年，专注于提供高清蓝光电影在线观看服务，收录超过10000部电影资源，每日更新，免广告不卡顿。",
            isPartOf: {
              "@type": "WebSite",
              name: "天堂影视",
              url: "https://tiantangmovies.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://tiantangmovies.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "关于我们",
                  item: "https://tiantangmovies.com/about",
                },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}